import { Building2, Wallet, ShieldCheck, CheckCircle2, Clock, Percent } from "lucide-react";
import zensumLogo from "@/assets/zensum_stor.webp";
import ucLogo from "@/assets/UC.png";
import bankTransferIcon from "@/assets/banköverföring.png";
import creditsafeImg from "@/assets/creditsafe.png";
import { ProviderData } from "@/types/provider";

export const zensumData: ProviderData = {
  // Basic Info
  id: "zensum",
  name: "Zensum",
  slug: "zensum",
  logo: zensumLogo,
  logoSize: "small",
  type: "comparison",
  numberOfLenders: 20,
  
  // Trustpilot
  trustpilot: {
    rating: 4.8,
    reviews: 6090,
    url: "https://se.trustpilot.com/review/zensum.se"
  },
  
  // Hero Section
  hero: {
    badges: [
      { text: "Jämför 20 långivare med en ansökan", variant: "premium" }
    ],
    description: "Zensum AB grundades 2014 och är en låneförmedlare som hjälper privatpersoner att jämföra privatlån från 20 olika långivare. Med endast en ansökan kan du få erbjudanden från flera banker och låneinstitut, vilket gör det enkelt att hitta bästa lånevillkor. Zensum står under tillsyn av Finansinspektionen och är medlem i branschorganisationen Creditsafe.",
    ctaButtonText: "Ansök hos Zensum",
    ctaButtonVariant: "default"
  },
  
  // Snabbfakta (Quick Facts) - 6 kort
  stats: [
    {
      icon: Building2,
      value: "20 långivare",
      label: "Jämför erbjudanden",
      description: "Med en ansökan"
    },
    {
      icon: Wallet,
      value: "20 000 – 600 000 kr",
      label: "Lånebelopp",
      description: "Flexibla belopp"
    },
    {
      icon: Percent,
      value: "5,05 – 33,23 %",
      label: "Effektiv ränta",
      description: "Varierar"
    },
    {
      icon: Clock,
      value: "1 – 15 år",
      label: "Löptid",
      description: "Flexibel återbetalningstid"
    },
    {
      icon: ShieldCheck,
      value: "UC",
      label: "Kreditupplysning",
      description: "Påverkar UC-Score"
    },
    {
      icon: CheckCircle2,
      value: "Ja",
      label: "Betalningsanmärkningar",
      description: "Hos vissa långivare"
    }
  ],
  
  // Features (Pros & Cons)
  features: {
    pros: [
      "Jämför 20 långivare med endast en ansökan",
      "Kostnadsfri förtidsinlösen",
      "Flexibla återbetalningsvillkor"
    ],
    cons: [
      "Inget samarbete med storkbanker"
    ]
  },
  
  // Key Information Grid - 4 kort
  keyInformation: [
    {
      image: ucLogo,
      title: "Kreditupplysning",
      value: "UC"
    },
    {
      image: bankTransferIcon,
      title: "Utbetalning via",
      value: "Banköverföring"
    },
    {
      icon: CheckCircle2,
      title: "Effektiv ränta",
      value: "5,05 – 33,23 %",
      description: "Varierar"
    },
    {
      image: creditsafeImg,
      title: "Medlemskap",
      value: "Creditsafe"
    }
  ],
  
  // Terms and Conditions
  termsAndConditions: [
    { term: "Lånebelopp", value: "20 000 – 600 000 kr" },
    { term: "Nominell ränta", value: "2,95 – 29,06 %" },
    { term: "Effektiv ränta", value: "5,05 – 33,23 %" },
    { term: "Löptid", value: "1 – 15 år" },
    { term: "Uppläggningsavgift", value: "(0–995 kr) Varierar mellan långivare" },
    { term: "Fakturaavgift (Pappersfaktura)", value: "Varierar mellan långivare" },
    { term: "Fakturaavgift (Autogiro)", value: "Varierar mellan långivare" },
    { 
      term: "Lån utan UC", 
      value: "Nej",
      badge: { text: "Nej", variant: "default" }
    },
    { term: "Svar på ansökan", value: "Samma dag" },
    { term: "Betalningslösning", value: "Banköverföring" }
  ],
  
  // Loan Requirements
  loanRequirements: [
    { 
      requirement: "Legitimering", 
      value: "Nej",
      badge: { text: "Nej", variant: "success" }
    },
    { 
      requirement: "Folkbokförd i Sverige", 
      value: "Ja, minst 1 år",
      badge: { text: "Ja, minst 1 år", variant: "info" }
    },
    { 
      requirement: "Svenskt personnummer", 
      value: "Ja",
      badge: { text: "Ja", variant: "info" }
    },
    { 
      requirement: "Svenskt mobilnummer", 
      value: "Ja",
      badge: { text: "Ja", variant: "info" }
    },
    { 
      requirement: "Svenskt kontonummer", 
      value: "Ja",
      badge: { text: "Ja", variant: "info" }
    },
    { requirement: "Minsta årsinkomst", value: "120 000 kr" },
    { 
      requirement: "Betalningsanmärkning OK?", 
      value: "Ja, hos vissa långivare",
      badge: { text: "Ja, hos vissa långivare", variant: "success" }
    },
    { requirement: "Ålderskrav", value: "18 +" },
    { 
      requirement: "Skuldsaldo Kronofogden", 
      value: "Nej",
      badge: { text: "Nej", variant: "default" }
    }
  ],
  
  // Interest Rate Section
  interestRateSection: {
    title: "Zensums ränta och avgifter",
    description: "Att ansöka om lån via Zensum är kostnadsfritt. Räntan och avgifterna bestäms av de långivare som Zensum samarbetar med. När du gjort en låneansökan bedömer långivarna din kreditvärdighet, och lånevillkoren blir individuellt anpassade.",
    rateTable: [
      { label: "Lånebelopp (lägsta – högsta)", value: "20 000 – 600 000 kr" },
      { label: "Nominell ränta", value: "2,95 – 29,06 %" },
      { label: "Effektiv ränta", value: "5,05 – 33,23 %" },
      { label: "Uppläggningsavgift", value: "Varierar mellan långivare" },
      { label: "Aviavgift", value: "Varierar mellan långivare" },
      { label: "Förtidsinlösen", value: "Kostnadsfri" }
    ]
  },
  
  // Payout Section
  payoutSection: {
    title: "Zensums utbetalning",
    description: "För privatlån tar utbetalning mellan 1 timme upp till 5 dagar beroende på långivare. Zensum kan inte påverka handläggningstiden hos långivarna, men om du signerar via BankID går det oftast snabbare än om du signerar via brev.",
    payoutTable: [
      { timeframe: "Snabbast", duration: "1 timme" },
      { timeframe: "Genomsnitt", duration: "1-5 dagar" }
    ],
    disclaimer: "Observera: Utbetalningar sker endast under bankdagar. Tiden kan variera beroende på vilken långivare som beviljar lånet och hur du väljer att signera avtalet."
  },
  
  // FAQ - 10 frågor
  faq: [
    {
      question: "Accepterar långivarna hos Zensum betalningsanmärkningar?",
      answer: "Ja, ett fåtal långivare som Zensum samarbetar med kan acceptera betalningsanmärkningar om de bedömer att du har god betalningsförmåga idag. Möjligheten varierar mellan långivare."
    },
    {
      question: "Kan man låna utan UC via Zensum?",
      answer: "Nej, alla långivare som Zensum samarbetar med kräver en kreditupplysning från UC. Detta görs för att bedöma din kreditvärdighet och betalningsförmåga."
    },
    {
      question: "När betalas lånet ut?",
      answer: "För privatlån tar utbetalning mellan 1 timme upp till 5 dagar beroende på långivare. Zensum kan inte påverka handläggningstiden hos långivarna, men om du signerar via BankID går det oftast snabbare än om du signerar via brev."
    },
    {
      question: "Kan jag ansöka om privatlån utan fast inkomst?",
      answer: "Ja, du kan ansöka utan fast inkomst hos vissa av Zensums långivare. Räntan kan dock bli högre då du anses som större risk."
    },
    {
      question: "Kostar det något att använda Zensum?",
      answer: "Nej, det är gratis att använda Zensum. De tar provision från långivaren som beviljar ditt lån. Kostnader kan tillkomma hos den långivare du väljer."
    },
    {
      question: "Vilka typer av lån jämför Zensum?",
      answer: "Zensum jämför privatlån från 20 olika långivare. Alla lån är utan säkerhet och kan användas för vilket syfte som helst."
    },
    {
      question: "Kan jag sänka ränta på ett befintligt lån hos Zensum?",
      answer: "Ja, du väljer själv vilka lån du vill jämföra och omförhandla. Zensum kommer att försöka hitta bättre villkor på ditt befintliga lån."
    },
    {
      question: "Kan jag lösa lånet i förtid hos Zensum?",
      answer: "Du kan när som helst amortera på lånet utan extra kostnader. Om du har fast ränta kan du behöva betala ränteskillnadsersättning när du löser ett lån i förtid."
    },
    {
      question: "Är min ränta fast eller rörlig hos Zensum?",
      answer: "Alla långivare Zensum samarbetar med erbjuder rörlig ränta på privatlån. Det innebär att räntan kan förändras, men inte att räntan på ditt befintliga lån kan höjas hur som helst. I ditt låneavtal finns den ränta som du och långivaren kommit överens om. För att långivaren ska få höja den räntan krävs det att upplåningskostnaderna ökar, dvs. de kostnader som långivaren har för att kunna låna ut pengar. En sådan ökad kostnad kan t.ex. vara att Riksbanken höjer sin styrränta (reporäntan)."
    },
    {
      question: "Förmedlar Zensum både annuitetslån och lån med rak amortering?",
      answer: "Ja, via Zensum kan du antingen bli beviljad lån med rak amortering eller ett annuitetslån. Majoriteten av Zensums långivare arbetar dock med annuitetslån. Vid ett annuitetslån betalar du alltid samma månadsbelopp. Det innebär att allt eftersom skulden minskar, så ökar andelen du amorterar på lånet."
    }
  ],
  
  // Customer Service
  customerService: {
    description: "Du kan kontakta Zensums kundtjänst via telefon eller mejl. De garanterar svar på frågor inom 48 timmar. Innan du kontaktar Zensum kan du se om du hittar svaret på din fråga på deras supportsida.",
    phone: "0771-30 44 47",
    phoneHours: ["Måndag till Torsdag: 09-18", "Fredag: 09-17", "Helger: Stängt"],
    email: "kundservice@zensum.se",
    emailHours: ["Måndag till Torsdag: 09-18", "Fredag: 09-17", "Helger: Stängt"]
  },
  
  // Company Info
  companyInfo: {
    name: "Zensum AB",
    orgNumber: "556953-5742",
    address: "Eriksbergsgatan 31",
    city: "114 30 Stockholm",
    website: "https://www.zensum.se",
    fiRegisterUrl: "https://www.fi.se/sv/vara-register/foretagsregistret/details?id=126335"
  },
  
  // Affiliate Link
  affiliateLink: {
    linkId: "zensum-affiliate-link-id",
    trackingSource: "zensum_page",
    url: "https://go.adt212.net/t/t?a=1548368523&as=2024680921&t=2&tk=1"
  },
  
  // SEO
  seo: {
    title: "Zensum Privatlån - Jämför 20 långivare | Klarfinans",
    description: "Jämför privatlån från 20 långivare via Zensum. Låna 20 000 - 600 000 kr. Nominell ränta 2,95% - 29,06%. Endast en UC-kreditupplysning. Ansök idag!",
    keywords: ["zensum", "privatlån", "jämför lån", "låneförmedlare", "20 långivare", "samlingslån"]
  },
  
  // Schema.org data
  schema: {
    productName: "Zensum Privatlån - Jämför 20 långivare",
    description: "Jämför privatlån från 20 olika långivare med en ansökan. Låna 20 000 - 600 000 kr. Flexibla återbetalningsvillkor. Svar snabbt.",
    url: "https://klarfinans.se/sv-SE/privatlan/zensum",
    provider: "Zensum",
    minAmount: 20000,
    maxAmount: 600000,
    featureList: [
      "Jämför lån från 20 olika långivare med en ansökan",
      "Lån från 20 000 kr upp till 600 000 kr",
      "Nominell ränta 2,95% - 29,06%",
      "Effektiv ränta 5,05% - 33,23%",
      "Flexibla återbetalningsvillkor",
      "Kostnadsfri förtidsinlösen",
      "Endast en UC-kreditupplysning trots flera erbjudanden",
      "Svar snabbt - ofta inom samma dag"
    ]
  }
};
